"use client";

import { notify, confirmDialog } from "@/components/ui/dialogs";
import { DatePicker } from "@/components/ui/date-picker";
import PageHeader from "@/components/layout/PageHeader";
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ViewIcon,
  Delete02Icon,
  Edit02Icon,
  FloppyDiskIcon,
  Search01Icon,
  Call02Icon,
  UserIcon,
  Calendar01Icon,
  PlusSignCircleIcon,
  AlertDiamondIcon,
} from "@hugeicons/core-free-icons";
import { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider,
  DataTableHeader,
  DataTableHeaderGroup,
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  TableActions,
  TableDropdown,
  TableDropdownButton,
  TableDropdownMenu,
  TableDropdownItem,
  useTableDropdown,
} from "../components/ui/data-table";
import { get, put, del, post } from "../utils/service";
import Drawer from "@/components/ui/Drawer";

interface Client {
  id: string;
  name: string;
  phone: string | null;
}

interface LoanPlan {
  id: string;
  name: string;
  duration_days: number;
  interest_rate: string;
  is_active: boolean;
}

interface LoanCharge {
  id: string;
  loan_id: string;
  amount: string;
  reason: string | null;
  created_at: string;
  creator?: { first_name: string; last_name: string };
}

interface LoanRedemption {
  id: string;
  loan_id: string;
  amount: string;
  redeemed_date: string;
  notes: string | null;
  created_at: string;
  recorder?: { first_name: string; last_name: string };
}

interface Loan {
  id: string;
  client_id: string;
  loan_plan_id: string | null;
  principal: string;
  interest_rate: string;
  duration_days: number;
  expected_return: string;
  start_date: string;
  due_date: string;
  status: string;
  currency: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  days_elapsed: number;
  days_remaining: number;
  days_overdue: number;
  remaining_percentage: number;
  effective_status: "active" | "due_soon" | "expiring_today" | "expired" | "redeemed" | "cancelled";
  needs_follow_up: boolean;
  additional_charges: string;
  amount_redeemed: string;
  current_amount_due: string;
  client?: Client;
  loan_plan?: LoanPlan;
  charges?: LoanCharge[];
  redemptions?: LoanRedemption[];
}

interface LoansResponse {
  success: boolean;
  message: string;
  data: Loan[];
  pagination: {
    total: number;
    current_page: number;
    per_page: number;
    total_pages: number;
  };
}

interface PaginationState {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const STATUS_COLORS: Record<string, string> = {
  active: "bg-gray-100 text-gray-800",
  due_soon: "bg-amber-100 text-amber-800",
  expiring_today: "bg-orange-100 text-orange-800",
  expired: "bg-red-100 text-red-800",
  redeemed: "bg-green-100 text-green-800",
  cancelled: "bg-gray-100 text-gray-500",
};

const formatLabel = (value: string) =>
  value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

// A fully repaid loan is shown as "Paid" (the stored status value stays "redeemed").
const statusLabel = (status: string) => (status === "redeemed" ? "Paid" : formatLabel(status));

const CLIENT_PAGE_SIZE = 5;

const formatCurrency = (amount: number | string | null, currency: string = "UGX") => {
  if (amount === null || amount === undefined) return `${currency} 0`;
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return `${currency} 0`;
  return `${currency} ${numAmount.toLocaleString()}`;
};

const addDays = (dateStr: string, days: number) => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + days);
  return d;
};

const SkeletonRow = () => (
  <div className="animate-pulse p-6">
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded"></div>
  </div>
);

const FloatingDrawer = ({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  const drawerPosition = isMobile || isTablet ? "bottom" : "right";

  if (drawerPosition === "bottom") {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} position="bottom" heightPercentage={85} showCloseButton={false}>
        <div className="h-full flex flex-col bg-black text-white">{children}</div>
      </Drawer>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 z-[99998]"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed top-4 right-4 bottom-4 w-96 bg-black border border-gray-800 shadow-2xl rounded-2xl z-[99999] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col bg-black text-white">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

interface CreateFormState {
  client_id: string;
  loan_plan_id: string;
  principal: string;
  interest_rate: string;
  duration_days: string;
  start_date: string;
  currency: string;
  notes: string;
}

const AddLoanForm = ({
  isOpen,
  onClose,
  onSuccess,
  defaultClient,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultClient?: Client | null;
}) => {
  const [formData, setFormData] = useState<CreateFormState>({
    client_id: "",
    loan_plan_id: "",
    principal: "",
    interest_rate: "",
    duration_days: "",
    start_date: new Date().toISOString().slice(0, 10),
    currency: "UGX",
    notes: "",
  });
  const [clients, setClients] = useState<Client[]>([]);
  const [clientPage, setClientPage] = useState(1);
  const [clientTotalPages, setClientTotalPages] = useState(1);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", phone: "" });
  const [creatingClient, setCreatingClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [plans, setPlans] = useState<LoanPlan[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    get<{ success: boolean; data: { loan_plans: LoanPlan[] } }>("/loan-plans")
      .then((r) => setPlans((r.data.loan_plans || []).filter((p) => p.is_active)))
      .catch(() => setPlans([]));
    if (defaultClient) {
      setSelectedClient(defaultClient);
      setFormData((prev) => ({ ...prev, client_id: defaultClient.id }));
    }
  }, [isOpen, defaultClient]);

  useEffect(() => {
    if (!isOpen || selectedClient) return;
    let cancelled = false;
    setClientsLoading(true);
    get<{ success: boolean; data: Client[]; pagination?: { total_pages: number } }>(
      `/clients?status=active&page=${clientPage}&page_size=${CLIENT_PAGE_SIZE}`
    )
      .then((response) => {
        if (cancelled || !response.success) return;
        setClients(response.data || []);
        setClientTotalPages(Math.max(response.pagination?.total_pages ?? 1, 1));
      })
      .catch((error) => console.error("Error loading clients:", error))
      .finally(() => {
        if (!cancelled) setClientsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedClient, clientPage]);

  const handleCreateClient = async () => {
    if (!newClient.name.trim() || !newClient.phone.trim()) return;
    setCreatingClient(true);
    try {
      const response = await post<{ success: boolean; data: { client: Client } }>("/clients", {
        name: newClient.name.trim(),
        phone: newClient.phone.trim(),
      });
      if (response.success) {
        setSelectedClient(response.data.client);
        setFormData((prev) => ({ ...prev, client_id: response.data.client.id }));
        setShowNewClient(false);
        setNewClient({ name: "", phone: "" });
        notify("Client added");
      }
    } catch (error) {
      console.error("Error adding client:", error);
      notify("Could not add the client. Check the name and phone number.");
    } finally {
      setCreatingClient(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: "",
      loan_plan_id: "",
      principal: "",
      interest_rate: "",
      duration_days: "",
      start_date: new Date().toISOString().slice(0, 10),
      currency: "UGX",
      notes: "",
    });
    setSelectedClient(null);
    setClientPage(1);
    setShowNewClient(false);
    setNewClient({ name: "", phone: "" });
  };

  const handlePlanSelect = (planId: string) => {
    const plan = plans.find((p) => p.id === planId);
    setFormData((prev) => ({
      ...prev,
      loan_plan_id: planId,
      interest_rate: plan ? String(plan.interest_rate) : prev.interest_rate,
      duration_days: plan ? String(plan.duration_days) : prev.duration_days,
    }));
  };

  const principalNum = parseFloat(formData.principal) || 0;
  const rateNum = parseFloat(formData.interest_rate) || 0;
  const previewExpectedReturn = principalNum + (principalNum * rateNum) / 100;
  const previewDueDate = formData.duration_days
    ? addDays(formData.start_date, parseInt(formData.duration_days, 10) || 0)
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClient) return;
    setIsSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        client_id: selectedClient.id,
        principal: principalNum,
        start_date: formData.start_date,
        currency: formData.currency,
        notes: formData.notes || undefined,
      };
      if (formData.loan_plan_id) payload.loan_plan_id = formData.loan_plan_id;
      if (!formData.loan_plan_id || formData.interest_rate) payload.interest_rate = rateNum;
      if (!formData.loan_plan_id || formData.duration_days) payload.duration_days = parseInt(formData.duration_days, 10);

      const response = await post("/loans", payload);
      if ((response as { success: boolean }).success) {
        onSuccess();
        onClose();
        resetForm();
      }
    } catch (error) {
      console.error("Error creating loan:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">New Loan</h2>
          <p className="text-xs text-gray-400 mt-1">Disburse a new loan to a client</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Client *</label>
          {selectedClient ? (
            <div className="bg-gray-900 rounded-lg p-3 flex items-start justify-between border border-gray-800">
              <div>
                <p className="text-xs font-semibold text-white">{selectedClient.name}</p>
                {selectedClient.phone && <p className="text-xs text-gray-400 mt-0.5">{selectedClient.phone}</p>}
              </div>
              <button type="button" onClick={() => { setSelectedClient(null); setFormData((p) => ({ ...p, client_id: "" })); }} className="text-xs text-gray-400 hover:text-white">
                Change
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {clientsLoading ? (
                <p className="py-3 text-center text-xs text-gray-500">Loading clients...</p>
              ) : clients.length === 0 ? (
                <p className="py-3 text-center text-xs text-gray-500">No clients yet. Add one below.</p>
              ) : (
                clients.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      setSelectedClient(c);
                      setFormData((prev) => ({ ...prev, client_id: c.id }));
                    }}
                    className="w-full text-left bg-gray-900 border border-gray-800 rounded-lg p-3 hover:border-secondary transition-colors"
                  >
                    <p className="text-xs font-semibold text-white">{c.name}</p>
                    <p className="text-xs text-gray-400">{c.phone}</p>
                  </button>
                ))
              )}

              {clientTotalPages > 1 && (
                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    disabled={clientPage <= 1 || clientsLoading}
                    onClick={() => setClientPage((p) => p - 1)}
                    className="px-3 py-1.5 text-xs rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-gray-400">
                    Page {clientPage} of {clientTotalPages}
                  </span>
                  <button
                    type="button"
                    disabled={clientPage >= clientTotalPages || clientsLoading}
                    onClick={() => setClientPage((p) => p + 1)}
                    className="px-3 py-1.5 text-xs rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}

              {showNewClient ? (
                <div className="bg-gray-900 rounded-lg p-3 border border-gray-800 space-y-3">
                  <p className="text-xs font-semibold text-white">New client</p>
                  <input
                    type="text"
                    value={newClient.name}
                    onChange={(e) => setNewClient((prev) => ({ ...prev, name: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleCreateClient();
                      }
                    }}
                    placeholder="Full name *"
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white placeholder:text-gray-500"
                  />
                  <input
                    type="tel"
                    value={newClient.phone}
                    onChange={(e) => setNewClient((prev) => ({ ...prev, phone: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleCreateClient();
                      }
                    }}
                    placeholder="Phone *"
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white placeholder:text-gray-500"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleCreateClient}
                      disabled={creatingClient || !newClient.name.trim() || !newClient.phone.trim()}
                      className="flex-1 px-3 py-2 bg-secondary text-primary text-xs font-semibold rounded-lg hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {creatingClient ? "Adding..." : "Add & select"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewClient(false);
                        setNewClient({ name: "", phone: "" });
                      }}
                      className="flex-1 px-3 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNewClient(true)}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-secondary border border-dashed border-gray-700 rounded-lg hover:border-secondary transition-colors"
                >
                  <HugeiconsIcon icon={PlusSignCircleIcon} size={14} />
                  Add new client
                </button>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Loan Plan <span className="font-normal text-gray-400">(optional)</span></label>
          <select
            value={formData.loan_plan_id}
            onChange={(e) => handlePlanSelect(e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white"
          >
            <option value="">Custom terms</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.interest_rate}% / {p.duration_days}d)
              </option>
            ))}
          </select>
        </div>

        {/* Currency is fixed to UGX for now (formData.currency), so there is no picker. */}
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Principal *</label>
          <input
            type="number"
            min={0}
            required
            value={formData.principal}
            onChange={(e) => setFormData((prev) => ({ ...prev, principal: e.target.value }))}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Interest Rate (%) *</label>
            <input
              type="number"
              step="0.01"
              min={0}
              required
              value={formData.interest_rate}
              onChange={(e) => setFormData((prev) => ({ ...prev, interest_rate: e.target.value }))}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Duration (days) *</label>
            <input
              type="number"
              min={1}
              required
              value={formData.duration_days}
              onChange={(e) => setFormData((prev) => ({ ...prev, duration_days: e.target.value }))}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Start Date *</label>
          <DatePicker dark value={formData.start_date} onChange={(v) => setFormData((prev) => ({ ...prev, start_date: v }))} required />
        </div>

        {(previewExpectedReturn > 0 || previewDueDate) && (
          <div className="bg-gray-900 rounded-lg p-3 border border-gray-800 space-y-1">
            <p className="text-xs text-gray-400">
              Expected return: <span className="text-white font-semibold">{formatCurrency(previewExpectedReturn, formData.currency)}</span>
            </p>
            {previewDueDate && (
              <p className="text-xs text-gray-400">
                Due date: <span className="text-white font-semibold">{previewDueDate.toLocaleDateString()}</span>
              </p>
            )}
          </div>
        )}

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
            rows={2}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white"
          />
        </div>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedClient || !formData.principal || !formData.interest_rate || !formData.duration_days || !formData.start_date}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-secondary text-primary text-xs font-semibold rounded-lg hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <HugeiconsIcon icon={FloppyDiskIcon} size={16} />
              Create Loan
            </>
          )}
        </button>
        <button
          onClick={() => { resetForm(); onClose(); }}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const EditLoanForm = ({
  isOpen,
  onClose,
  onSuccess,
  loan,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  loan: Loan | null;
}) => {
  const [formData, setFormData] = useState({ notes: "", status: "active", start_date: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (loan) {
      setFormData({
        notes: loan.notes || "",
        status: loan.status === "redeemed" ? "active" : loan.status,
        start_date: loan.start_date?.slice(0, 10) || "",
      });
    }
  }, [loan]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loan) return;
    setIsSubmitting(true);
    try {
      const response = await put(`/loans/${loan.id}`, formData);
      if ((response as { success: boolean }).success) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Error updating loan:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!loan) return null;

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Edit Loan</h2>
          <p className="text-xs text-gray-400 mt-1">{loan.client?.name}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Start Date</label>
          <DatePicker dark value={formData.start_date} onChange={(v) => setFormData((prev) => ({ ...prev, start_date: v }))} />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white"
          >
            <option value="active">Active</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-white"
          />
        </div>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-secondary text-primary text-xs font-semibold rounded-lg hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <HugeiconsIcon icon={FloppyDiskIcon} size={16} />
              Update Loan
            </>
          )}
        </button>
        <button onClick={onClose} className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors">
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const AddChargeModal = ({ isOpen, onClose, onSuccess, loanId }: { isOpen: boolean; onClose: () => void; onSuccess: () => void; loanId: string | null }) => {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loanId) return;
    setIsSubmitting(true);
    try {
      const response = await post(`/loans/${loanId}/charges`, { amount: parseFloat(amount), reason: reason || undefined });
      if ((response as { success: boolean }).success) {
        onSuccess();
        onClose();
        setAmount("");
        setReason("");
      }
    } catch (error) {
      console.error("Error adding charge:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-black border border-gray-800 rounded-2xl w-full max-w-sm p-6">
        <h3 className="text-xs font-bold text-white mb-4">Add Overdue Charge</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Amount *</label>
            <input
              type="number"
              min={0}
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary text-white"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Reason</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary text-white"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={isSubmitting || !amount} className="flex-1 px-4 py-2 bg-secondary text-primary text-xs font-semibold rounded-lg hover:bg-secondary/90 disabled:opacity-50">
              {isSubmitting ? "Adding..." : "Add Charge"}
            </button>
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-gray-300 bg-gray-800 text-xs font-semibold rounded-lg hover:bg-gray-700">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PayLoanModal = ({ isOpen, onClose, onSuccess, loanId }: { isOpen: boolean; onClose: () => void; onSuccess: () => void; loanId: string | null }) => {
  const [amount, setAmount] = useState("");
  const [redeemedDate, setRedeemedDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loanId) return;
    setIsSubmitting(true);
    try {
      const response = await post(`/loans/${loanId}/redeem`, { amount: parseFloat(amount), redeemed_date: redeemedDate, notes: notes || undefined });
      if ((response as { success: boolean }).success) {
        onSuccess();
        onClose();
        setAmount("");
        setNotes("");
      }
    } catch (error) {
      console.error("Error recording payment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-black border border-gray-800 rounded-2xl w-full max-w-sm p-6">
        <h3 className="text-xs font-bold text-white mb-4">Pay Loan</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Amount *</label>
            <input
              type="number"
              min={0}
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary text-white"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Date</label>
            <DatePicker dark value={redeemedDate} onChange={setRedeemedDate} />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Notes</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary text-white"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={isSubmitting || !amount} className="flex-1 px-4 py-2 bg-secondary text-primary text-xs font-semibold rounded-lg hover:bg-secondary/90 disabled:opacity-50">
              {isSubmitting ? "Saving..." : "Pay Loan"}
            </button>
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-gray-300 bg-gray-800 text-xs font-semibold rounded-lg hover:bg-gray-700">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const LoanDrawer = ({
  loan,
  isOpen,
  onClose,
  onDelete,
  onEdit,
  onAddCharge,
  onPay,
}: {
  loan: Loan | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (id: string) => void;
  onEdit: () => void;
  onAddCharge: () => void;
  onPay: () => void;
}) => {
  if (!loan) return null;

  const charges = loan.charges || [];
  const redemptions = loan.redemptions || [];

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Loan Details</h2>
          <p className="text-xs text-gray-400 mt-1">{loan.client?.name}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-lg ${STATUS_COLORS[loan.effective_status] || "bg-gray-100 text-gray-800"}`}>
            {statusLabel(loan.effective_status)}
          </span>
          {loan.needs_follow_up && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              <HugeiconsIcon icon={AlertDiamondIcon} size={12} /> Follow up
            </span>
          )}
        </div>

        {loan.effective_status !== "redeemed" && loan.effective_status !== "cancelled" && (
          <div>
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Time remaining</span>
              <span>{loan.days_remaining > 0 ? `${loan.days_remaining} days left` : loan.days_overdue > 0 ? `${loan.days_overdue} days overdue` : "Due today"}</span>
            </div>
            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${loan.remaining_percentage <= 15 ? "bg-red-500" : loan.remaining_percentage <= 40 ? "bg-amber-500" : "bg-green-500"}`}
                style={{ width: `${Math.max(0, Math.min(100, loan.remaining_percentage))}%` }}
              />
            </div>
          </div>
        )}

        <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
          <p className="text-xs text-gray-400">Current Amount Due</p>
          <p className="text-xl font-bold text-white mt-1">{formatCurrency(loan.current_amount_due, loan.currency)}</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-400">Principal</p>
            <p className="text-xs font-semibold text-white">{formatCurrency(loan.principal, loan.currency)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Interest Rate</p>
            <p className="text-xs font-semibold text-white">{loan.interest_rate}%</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Expected Return</p>
            <p className="text-xs font-semibold text-white">{formatCurrency(loan.expected_return, loan.currency)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Duration</p>
            <p className="text-xs font-semibold text-white">{loan.duration_days} days</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Start Date</p>
            <p className="text-xs font-semibold text-white">{new Date(loan.start_date).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Due Date</p>
            <p className="text-xs font-semibold text-white">{new Date(loan.due_date).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Additional Charges</p>
            <p className="text-xs font-semibold text-white">{formatCurrency(loan.additional_charges, loan.currency)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Amount Paid</p>
            <p className="text-xs font-semibold text-white">{formatCurrency(loan.amount_redeemed, loan.currency)}</p>
          </div>
        </div>

        {loan.client && (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <HugeiconsIcon icon={UserIcon} size={16} className="text-gray-400" />
              <p className="text-xs text-white">{loan.client.name}</p>
            </div>
            {loan.client.phone && (
              <div className="flex items-center gap-3">
                <HugeiconsIcon icon={Call02Icon} size={16} className="text-gray-400" />
                <p className="text-xs text-gray-400">{loan.client.phone}</p>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <button onClick={onAddCharge} className="px-3 py-2 bg-gray-800 text-white text-xs rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-1">
            <HugeiconsIcon icon={PlusSignCircleIcon} size={14} /> Add Charge
          </button>
          <button onClick={onPay} className="px-3 py-2 bg-secondary text-primary text-xs rounded-lg hover:bg-secondary/90 transition-colors flex items-center justify-center gap-1">
            <HugeiconsIcon icon={FloppyDiskIcon} size={14} /> Pay Loan
          </button>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Charges ({charges.length})</label>
          {charges.length === 0 ? (
            <p className="text-xs text-gray-500">No additional charges.</p>
          ) : (
            <div className="space-y-2">
              {charges.map((c) => (
                <div key={c.id} className="bg-gray-900 rounded-lg p-3 border border-gray-800 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-white">{formatCurrency(c.amount, loan.currency)}</p>
                    {c.reason && <p className="text-xs text-gray-400">{c.reason}</p>}
                  </div>
                  <p className="text-xs text-gray-500">{new Date(c.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Payments ({redemptions.length})</label>
          {redemptions.length === 0 ? (
            <p className="text-xs text-gray-500">No payments recorded.</p>
          ) : (
            <div className="space-y-2">
              {redemptions.map((r) => (
                <div key={r.id} className="bg-gray-900 rounded-lg p-3 border border-gray-800 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-white">{formatCurrency(r.amount, loan.currency)}</p>
                    {r.notes && <p className="text-xs text-gray-400">{r.notes}</p>}
                  </div>
                  <p className="text-xs text-gray-500">{new Date(r.redeemed_date).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {loan.notes && (
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Notes</label>
            <p className="text-xs text-gray-400">{loan.notes}</p>
          </div>
        )}

        <div className="flex items-center gap-3">
          <HugeiconsIcon icon={Calendar01Icon} size={16} className="text-gray-400" />
          <p className="text-xs text-gray-400">Created {new Date(loan.created_at).toLocaleDateString()}</p>
        </div>
      </div>

      <div className="p-3 border-t border-gray-800">
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onEdit} className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 text-white text-xs rounded-lg hover:bg-gray-700 transition-colors">
            <HugeiconsIcon icon={Edit02Icon} size={16} />
            Edit
          </button>
          <button
            onClick={async () => {
              if (await confirmDialog("Are you sure you want to delete this loan? This cannot be undone.")) {
                onDelete(loan.id);
              }
            }}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
          >
            <HugeiconsIcon icon={Delete02Icon} size={16} />
            Delete
          </button>
        </div>
      </div>
    </FloatingDrawer>
  );
};

function ActionsCell({ onView, onEdit, onDelete }: { onView: () => void; onEdit: () => void; onDelete: () => void }) {
  const { isOpen, position, handleOpen, handleClose } = useTableDropdown();

  return (
    <TableActions>
      <TableDropdown>
        <TableDropdownButton onClick={handleOpen} />
        <TableDropdownMenu isOpen={isOpen} onClose={handleClose} position={position}>
          <TableDropdownItem onClick={() => { onView(); handleClose(); }}>
            <HugeiconsIcon icon={ViewIcon} size={12} />
            View Details
          </TableDropdownItem>
          <TableDropdownItem onClick={() => { onEdit(); handleClose(); }}>
            <HugeiconsIcon icon={Edit02Icon} size={12} />
            Edit Loan
          </TableDropdownItem>
          <TableDropdownItem onClick={() => { onDelete(); handleClose(); }} className="text-red-600 hover:bg-red-50">
            <HugeiconsIcon icon={Delete02Icon} size={12} />
            Delete
          </TableDropdownItem>
        </TableDropdownMenu>
      </TableDropdown>
    </TableActions>
  );
}

function LoansPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") || "all");
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddCharge, setShowAddCharge] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({ page: 1, per_page: 10, total: 0, total_pages: 0 });

  const clientIdParam = searchParams.get("client_id");
  const highlightId = searchParams.get("highlight");
  const newParam = searchParams.get("new");

  const fetchLoans = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(pagination.page),
        page_size: String(pagination.per_page),
      });
      if (searchQuery.trim()) params.set("search", searchQuery.trim());
      if (clientIdParam) params.set("client_id", clientIdParam);
      if (statusFilter !== "all") {
        if (["due_soon", "expiring_today", "expired"].includes(statusFilter)) {
          params.set("filter", statusFilter);
        } else {
          params.set("status", statusFilter);
        }
      }
      const response = await get<LoansResponse>(`/loans?${params.toString()}`);
      if (response.success) {
        setLoans(response.data);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination?.total ?? 0,
          total_pages: response.pagination?.total_pages ?? 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching loans:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, searchQuery, statusFilter, clientIdParam]);

  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  useEffect(() => {
    if (highlightId) {
      get<{ success: boolean; data: { loan: Loan } }>(`/loans/${highlightId}`)
        .then((r) => {
          if (r.success) {
            setSelectedLoan(r.data.loan);
            setShowDrawer(true);
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId]);

  useEffect(() => {
    if (clientIdParam && !showAdd && !showDrawer) {
      setShowAdd(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIdParam]);

  useEffect(() => {
    if (newParam && !showAdd && !showDrawer) {
      setShowAdd(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newParam]);

  const refreshSelectedLoan = useCallback(async (id: string) => {
    try {
      const r = await get<{ success: boolean; data: { loan: Loan } }>(`/loans/${id}`);
      if (r.success) setSelectedLoan(r.data.loan);
    } catch (error) {
      console.error("Error refreshing loan:", error);
    }
  }, []);

  const handleViewLoan = async (loan: Loan) => {
    setSelectedLoan(loan);
    setShowDrawer(true);
    refreshSelectedLoan(loan.id);
  };

  const handleEditLoan = (loan: Loan) => {
    setSelectedLoan(loan);
    setShowEdit(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await del(`/loans/${id}`);
      setLoans((prev) => prev.filter((l) => l.id !== id));
      if (selectedLoan?.id === id) {
        setShowDrawer(false);
        setSelectedLoan(null);
      }
      notify("Loan deleted successfully");
    } catch (error) {
      console.error("Error deleting loan:", error);
      notify("Error deleting loan");
    }
  };

  const handleBatchDelete = async (rows: { id: string }[]) => {
    if (!await confirmDialog(`Delete ${rows.length} selected loans? This cannot be undone.`)) return;
    const results = await Promise.allSettled(rows.map((r) => del(`/loans/${r.id}`)));
    const deleted = new Set(rows.filter((_, i) => results[i].status === "fulfilled").map((r) => r.id));
    setLoans((prev) => prev.filter((item) => !deleted.has(item.id)));
    const failed = rows.length - deleted.size;
    notify(failed ? `${deleted.size} deleted, ${failed} failed` : `${deleted.size} loans deleted successfully`);
  };

  const closeAll = () => {
    setShowDrawer(false);
    setSelectedLoan(null);
    if (highlightId || clientIdParam) {
      searchParams.delete("highlight");
      searchParams.delete("client_id");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const columns: ColumnDef<Loan>[] = [
    {
      id: "client",
      header: "Client",
      cell: ({ row }) => (
        <div>
          <p className="text-xs font-semibold text-gray-900">{row.original.client?.name || "—"}</p>
          {row.original.client?.phone && <p className="text-xs text-gray-600">{row.original.client.phone}</p>}
        </div>
      ),
    },
    {
      accessorKey: "principal",
      header: "Principal",
      cell: ({ row }) => <p className="text-xs text-gray-700">{formatCurrency(row.original.principal, row.original.currency)}</p>,
    },
    {
      accessorKey: "interest_rate",
      header: "Interest Rate",
      cell: ({ row }) => <p className="text-xs text-gray-700">{row.original.interest_rate}%</p>,
    },
    {
      accessorKey: "expected_return",
      header: "Expected Return",
      meta: { hideOrder: 2 },
      cell: ({ row }) => <p className="text-xs text-gray-700">{formatCurrency(row.original.expected_return, row.original.currency)}</p>,
    },
    {
      accessorKey: "due_date",
      header: "Due Date",
      cell: ({ row }) => <p className="text-xs text-gray-700">{new Date(row.original.due_date).toLocaleDateString()}</p>,
    },
    {
      id: "remaining",
      header: "Time Left",
      meta: { hideOrder: 1 },
      cell: ({ row }) => {
        const loan = row.original;
        if (loan.effective_status === "redeemed" || loan.effective_status === "cancelled") {
          return <p className="text-xs text-gray-500">—</p>;
        }
        return (
          <div className="w-24">
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${loan.remaining_percentage <= 15 ? "bg-red-500" : loan.remaining_percentage <= 40 ? "bg-amber-500" : "bg-green-500"}`}
                style={{ width: `${Math.max(0, Math.min(100, loan.remaining_percentage))}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {loan.days_remaining > 0 ? `${loan.days_remaining}d left` : loan.days_overdue > 0 ? `${loan.days_overdue}d overdue` : "Due today"}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "effective_status",
      header: "Status",
      meta: { hideOrder: 3 },
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[row.original.effective_status] || "bg-gray-100 text-gray-800"}`}>
            {statusLabel(row.original.effective_status)}
          </span>
          {row.original.needs_follow_up && <HugeiconsIcon icon={AlertDiamondIcon} size={14} className="text-amber-500" />}
        </div>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <ActionsCell
          onView={() => handleViewLoan(row.original)}
          onEdit={() => handleEditLoan(row.original)}
          onDelete={() => handleDelete(row.original.id)}
        />
      ),
    },
  ];

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Loans" description="Track disbursements, follow-ups and payments">
          <button
            onClick={() => setShowAdd(true)}
            className="px-5 py-1.5 bg-gradient-to-r from-primary to-primary/90 text-white text-sm font-light tracking-wide rounded-full hover:from-black/90 hover:to-black transition-all duration-200 min-w-[140px]"
          >
            New Loan
          </button>
        </PageHeader>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <HugeiconsIcon icon={Search01Icon} size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by client name or phone..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="due_soon">Due Soon</option>
            <option value="expiring_today">Expiring Today</option>
            <option value="expired">Expired</option>
            <option value="redeemed">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden has-[[data-card-view]]:border-0 has-[[data-card-view]]:bg-transparent has-[[data-card-view]]:shadow-none">
          {loading ? (
            <SkeletonRow />
          ) : (
            <>
              <TableProvider columns={columns} data={loans} onDeleteSelected={handleBatchDelete} onAddRow={() => setShowAdd(true)} addRowLabel="New loan">
                <DataTableHeader>
                  {({ headerGroup }) => (
                    <DataTableHeaderGroup key={headerGroup.id} headerGroup={headerGroup}>
                      {({ header }) => <DataTableHead key={header.id} header={header} />}
                    </DataTableHeaderGroup>
                  )}
                </DataTableHeader>
                <DataTableBody>
                  {({ row }) => (
                    <DataTableRow key={row.id} row={row} className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                      {({ cell }) => <DataTableCell key={cell.id} cell={cell} />}
                    </DataTableRow>
                  )}
                </DataTableBody>
              </TableProvider>

              {loans.length === 0 && (
                <div className="text-center py-16">
                  <h3 className="text-lg font-light text-gray-900 mb-2">No loans found</h3>
                  <p className="text-sm font-light text-gray-500 mb-6 max-w-md mx-auto">
                    {searchQuery || statusFilter !== "all" ? "Adjust your search criteria or filters." : "Disburse your first loan to get started."}
                  </p>
                </div>
              )}

              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-600">
                    Showing {((pagination.page - 1) * pagination.per_page) + 1}–
                    {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} loans
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <span className="px-2 py-1 text-xs text-gray-600">Page {pagination.page} of {pagination.total_pages}</span>
                    <button
                      type="button"
                      onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                      disabled={pagination.page === pagination.total_pages}
                      className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <LoanDrawer
        loan={selectedLoan}
        isOpen={showDrawer}
        onClose={closeAll}
        onDelete={handleDelete}
        onEdit={() => {
          setShowDrawer(false);
          setShowEdit(true);
        }}
        onAddCharge={() => setShowAddCharge(true)}
        onPay={() => setShowPay(true)}
      />

      <AddLoanForm
        isOpen={showAdd}
        onClose={() => {
          setShowAdd(false);
          if (clientIdParam) {
            searchParams.delete("client_id");
            setSearchParams(searchParams, { replace: true });
          }
        }}
        onSuccess={() => {
          fetchLoans();
          notify("Loan created successfully");
        }}
        defaultClient={null}
      />

      <EditLoanForm
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        onSuccess={() => {
          fetchLoans();
          notify("Loan updated successfully");
          if (selectedLoan) refreshSelectedLoan(selectedLoan.id);
        }}
        loan={selectedLoan}
      />

      <AddChargeModal
        isOpen={showAddCharge}
        onClose={() => setShowAddCharge(false)}
        onSuccess={() => {
          fetchLoans();
          notify("Charge added successfully");
          if (selectedLoan) refreshSelectedLoan(selectedLoan.id);
        }}
        loanId={selectedLoan?.id || null}
      />

      <PayLoanModal
        isOpen={showPay}
        onClose={() => setShowPay(false)}
        onSuccess={() => {
          fetchLoans();
          notify("Payment recorded successfully");
          if (selectedLoan) refreshSelectedLoan(selectedLoan.id);
        }}
        loanId={selectedLoan?.id || null}
      />
    </div>
  );
}

export default LoansPage;
